import { useEffect, useMemo, useState } from 'react';
import {
  FileText,
  Plus,
  CreditCard as Edit2,
  Trash2,
  Upload,
  X,
  LayoutGrid,
  List,
  Braces,
  ChevronDown,
  ChevronUp,
  Loader,
  Check,
  Database,
  Cloud,
} from 'lucide-react';
import {
  invokeMarkdownAlgoliaEdge,
  parseMarkdownAlgoliaStatus,
  parseMarkdownIndexedStatus,
  supabase,
  type MarkdownFile,
  type MarkdownIndexedStatus,
} from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { DeleteModal } from '../components/DeleteModal';

interface DocumentsPageProps {
  user: User;
  onViewDocument: (documentId: string) => void;
}

type ViewMode = 'panel' | 'table';

const KB_VIEW_MODE_KEY = 'sop-ai:knowledge-base-view-mode';

/** Tag strings on the document (`markdown_files.tags` only), trimmed and deduped case-insensitively. */
function documentTagStrings(file: MarkdownFile): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of file.tags ?? []) {
    const label = raw.trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

/** Every distinct tag string that appears on any loaded document (`markdown_files.tags`). */
function uniqueDocumentTagsAcrossFiles(files: MarkdownFile[]): string[] {
  const byKey = new Map<string, string>();
  for (const file of files) {
    for (const label of documentTagStrings(file)) {
      const key = label.toLowerCase();
      if (!byKey.has(key)) byKey.set(key, label);
    }
  }
  return [...byKey.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

/** How many documents in `pool` include `label` (case-insensitive). */
function countDocsWithTag(pool: MarkdownFile[], label: string): number {
  const key = label.toLowerCase();
  let n = 0;
  for (const f of pool) {
    if (documentTagStrings(f).some((t) => t.toLowerCase() === key)) n++;
  }
  return n;
}

function getInitialViewMode(): ViewMode {
  try {
    const v = localStorage.getItem(KB_VIEW_MODE_KEY);
    if (v === 'panel' || v === 'table') return v;
  } catch {
    /* private mode / quota */
  }
  return 'panel';
}

const indexBadgeClass =
  'inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400';

const indexPendingBadgeClass =
  'inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200';

const indexFailedBadgeClass =
  'inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-900 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-200';

function statusChipClass(status: MarkdownIndexedStatus): string {
  if (status === 'yes') return indexBadgeClass;
  if (status === 'pending') return indexPendingBadgeClass;
  if (status === 'failed') return indexFailedBadgeClass;
  return indexBadgeClass;
}

function statusChipMeta(
  kind: 'algolia' | 'vector',
  status: MarkdownIndexedStatus,
): { title: string; label: string } {
  if (kind === 'algolia') {
    const title =
      status === 'yes'
        ? 'Synced to Algolia'
        : status === 'pending'
          ? 'Algolia sync in progress'
          : status === 'failed'
            ? 'Algolia sync failed'
            : 'Not in Algolia';
    const label =
      status === 'yes'
        ? 'Algolia'
        : status === 'pending'
          ? 'Algolia pending'
          : 'Algolia failed';
    return { title, label };
  }
  const title =
    status === 'yes'
      ? 'Vectorized for chat (embeddings ready)'
      : status === 'pending'
        ? 'Vectorization in progress'
        : 'Vectorization failed';
  const label =
    status === 'yes'
      ? 'Vectorized'
      : status === 'pending'
        ? 'Vector pending'
        : 'Vector failed';
  return { title, label };
}

/** Algolia + vector lifecycle chips (below document tags). */
function IndexStatusChips({ file }: { file: MarkdownFile }) {
  const pairs = [
    { kind: 'algolia' as const, status: parseMarkdownAlgoliaStatus(file.algolia) },
    { kind: 'vector' as const, status: parseMarkdownIndexedStatus(file.indexed) },
  ].filter((p) => p.status !== 'no');

  if (pairs.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {pairs.map(({ kind, status }) => {
        const { title, label } = statusChipMeta(kind, status);
        return (
          <span key={kind} className={statusChipClass(status)} title={title}>
            {status === 'pending' ? (
              <>
                <Loader className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
                {label}
              </>
            ) : status === 'yes' ? (
              <>
                <Check className="h-3 w-3 shrink-0 stroke-[2.5]" aria-hidden />
                {label}
              </>
            ) : (
              label
            )}
          </span>
        );
      })}
    </div>
  );
}

function hasIndexBadges(file: MarkdownFile): boolean {
  return (
    parseMarkdownIndexedStatus(file.indexed) !== 'no' ||
    parseMarkdownAlgoliaStatus(file.algolia) !== 'no'
  );
}

export function DocumentsPage({ user, onViewDocument }: DocumentsPageProps) {
  const [files, setFiles] = useState<MarkdownFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(() => getInitialViewMode());
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<MarkdownFile | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadContent, setUploadContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [tagFilterLabels, setTagFilterLabels] = useState<string[]>([]);
  const [jsonModalFile, setJsonModalFile] = useState<MarkdownFile | null>(null);
  /** When false, tags that appear on only one document in the current set are hidden (unless none are “common”). */
  const [tagRareExpanded, setTagRareExpanded] = useState(false);

  useEffect(() => {
    void loadEverything();
  }, [user.id]);

  useEffect(() => {
    try {
      localStorage.setItem(KB_VIEW_MODE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const loadEverything = async () => {
    setLoading(true);

    const { data: fileData, error: fileError } = await supabase
      .from('markdown_files')
      .select('*')
      .order('created_at', { ascending: false });

    if (fileError) {
      console.error('Error loading files:', fileError);
    } else {
      setFiles(fileData || []);
    }

    setLoading(false);
  };

  const allDocumentTagLabels = useMemo(() => uniqueDocumentTagsAcrossFiles(files), [files]);

  /** Full KB counts (`indexed` / `algolia` = yes), not tag-filtered. */
  const kbIndexStats = useMemo(() => {
    const total = files.length;
    let vectorized = 0;
    let algoliaSynced = 0;
    for (const f of files) {
      if (parseMarkdownIndexedStatus(f.indexed) === 'yes') vectorized++;
      if (parseMarkdownAlgoliaStatus(f.algolia) === 'yes') algoliaSynced++;
    }
    return { total, vectorized, algoliaSynced };
  }, [files]);

  useEffect(() => {
    const valid = new Set(allDocumentTagLabels.map((t) => t.toLowerCase()));
    setTagFilterLabels((prev) => prev.filter((l) => valid.has(l.toLowerCase())));
  }, [allDocumentTagLabels]);

  useEffect(() => {
    setTagRareExpanded(false);
  }, [allDocumentTagLabels, tagFilterLabels]);

  const filteredFiles = useMemo(() => {
    if (tagFilterLabels.length === 0) return files;
    return files.filter((f) => {
      const docLower = new Set(documentTagStrings(f).map((s) => s.toLowerCase()));
      return tagFilterLabels.some((label) => docLower.has(label.toLowerCase()));
    });
  }, [files, tagFilterLabels]);

  /** Document pool used for tag counts: all files, or the current filter match set when filters are on. */
  const filesForTagCounts = useMemo(
    () => (tagFilterLabels.length === 0 ? files : filteredFiles),
    [files, filteredFiles, tagFilterLabels.length],
  );

  const tagChipsWithCounts = useMemo(() => {
    const rows = allDocumentTagLabels.map((label) => ({
      label,
      count: countDocsWithTag(filesForTagCounts, label),
    }));
    rows.sort(
      (a, b) =>
        b.count - a.count ||
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
    return rows;
  }, [allDocumentTagLabels, filesForTagCounts]);

  const visibleTagChips = useMemo(
    () => tagChipsWithCounts.filter(({ count }) => count > 0),
    [tagChipsWithCounts],
  );
  const commonTagChips = useMemo(
    () => visibleTagChips.filter(({ count }) => count > 1),
    [visibleTagChips],
  );
  const rareTagChips = useMemo(
    () => visibleTagChips.filter(({ count }) => count === 1),
    [visibleTagChips],
  );

  const toggleTagFilter = (label: string) => {
    const key = label.toLowerCase();
    setTagFilterLabels((prev) => {
      const has = prev.some((p) => p.toLowerCase() === key);
      if (has) return prev.filter((p) => p.toLowerCase() !== key);
      return [...prev, label];
    });
  };

  const handleUpload = async () => {
    if (!uploadTitle.trim() || !uploadContent.trim()) {
      alert('Please provide both title and content');
      return;
    }

    const { data: inserted, error } = await supabase
      .from('markdown_files')
      .insert({
        title: uploadTitle,
        content: uploadContent,
        user_id: user?.id,
        indexed: 'no',
        algolia: 'no',
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } else {
      setUploadTitle('');
      setUploadContent('');
      setShowUploadModal(false);
      void loadEverything();
      if (inserted?.id) {
        const algolia = await invokeMarkdownAlgoliaEdge(inserted.id);
        if (!algolia.ok) {
          alert(`File uploaded but search index did not update: ${algolia.message}`);
        } else {
          void loadEverything();
        }
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith('.md')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setUploadContent(content);
        setUploadTitle(file.name.replace('.md', ''));
      };
      reader.readAsText(file);
    }
  };

  const handleEdit = async () => {
    if (!selectedFile || !editTitle.trim()) {
      alert('Please provide a title');
      return;
    }

    const { error } = await supabase
      .from('markdown_files')
      .update({
        title: editTitle,
        content: editContent,
        updated_at: new Date().toISOString(),
      })
      .eq('id', selectedFile.id);

    if (error) {
      console.error('Error updating file:', error);
      alert('Failed to update file');
    } else {
      const editedId = selectedFile.id;
      setShowEditModal(false);
      setSelectedFile(null);
      void loadEverything();
      const algolia = await invokeMarkdownAlgoliaEdge(editedId);
      if (!algolia.ok) {
        alert(`File saved but search index did not update: ${algolia.message}`);
      } else {
        void loadEverything();
      }
    }
  };

  const handleDelete = async (): Promise<boolean> => {
    if (!selectedFile) return false;

    const algolia = await invokeMarkdownAlgoliaEdge(selectedFile.id, {
      action: 'delete',
    });
    if (!algolia.ok) {
      alert(`Could not remove document from search: ${algolia.message}`);
      return false;
    }

    const { error } = await supabase
      .from('markdown_files')
      .delete()
      .eq('id', selectedFile.id);

    if (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
      return false;
    }

    void loadEverything();
    return true;
  };

  const openDeleteModal = (file: MarkdownFile) => {
    setSelectedFile(file);
    setShowDeleteModal(true);
  };

  const openEditModal = (file: MarkdownFile) => {
    setSelectedFile(file);
    setEditTitle(file.title);
    setEditContent(file.content);
    setShowEditModal(true);
  };

  const hasRareTagChips = rareTagChips.length > 0;
  const onlySingletonTags = commonTagChips.length === 0 && hasRareTagChips;
  const clipTagFirstRow = !tagRareExpanded && hasRareTagChips;

  const renderTagFilterChip = (label: string, count: number) => (
    <button
      key={label}
      type="button"
      onClick={() => toggleTagFilter(label)}
      title={`${count} document${count === 1 ? '' : 's'} in the current set`}
      aria-pressed={tagFilterLabels.some((l) => l.toLowerCase() === label.toLowerCase())}
      className={`inline-flex items-baseline gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors tabular-nums ${
        tagFilterLabels.some((l) => l.toLowerCase() === label.toLowerCase())
          ? 'border-slate-800 bg-slate-800 text-white dark:border-slate-200 dark:bg-slate-200 dark:text-slate-900'
          : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
      }`}
    >
      <span>{label}</span>
      <span
        className={
          tagFilterLabels.some((l) => l.toLowerCase() === label.toLowerCase())
            ? 'text-slate-300 dark:text-slate-600'
            : 'text-slate-500 dark:text-slate-400'
        }
      >
        ({count})
      </span>
    </button>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 dark:text-slate-100">
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-slate-700 dark:text-slate-300" />
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100">
            Knowledge Base
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
            <button
              onClick={() => setViewMode('panel')}
              className={`rounded p-2 transition-colors ${
                viewMode === 'panel'
                  ? 'bg-white text-slate-800 shadow-sm dark:bg-slate-900 dark:text-slate-100'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
              title="Panel view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`rounded p-2 transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-slate-800 shadow-sm dark:bg-slate-900 dark:text-slate-100'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
              title="Table view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-white transition-colors hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
          >
            <Plus className="w-5 h-5" />
            New File
          </button>
        </div>
      </div>

      {!loading && kbIndexStats.total > 0 && (
        <div
          className="mb-6 flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-800/80 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
          role="region"
          aria-label="Knowledge base indexing coverage"
        >
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 tabular-nums">
            <div
              className="flex items-center gap-2"
              title="Documents with embeddings ready (`indexed` = yes)"
            >
              <Database className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {kbIndexStats.vectorized}
              </span>
              <span className="text-slate-400">/</span>
              <span className="text-slate-600 dark:text-slate-300">{kbIndexStats.total}</span>
              <span className="text-slate-600 dark:text-slate-300">vectorized</span>
            </div>
            <div className="hidden h-4 w-px shrink-0 bg-slate-200 sm:block dark:bg-slate-600" aria-hidden />
            <div
              className="flex items-center gap-2"
              title="Documents synced to Algolia (`algolia` = yes)"
            >
              <Cloud className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {kbIndexStats.algoliaSynced}
              </span>
              <span className="text-slate-400">/</span>
              <span className="text-slate-600 dark:text-slate-300">{kbIndexStats.total}</span>
              <span className="text-slate-600 dark:text-slate-300">in Algolia</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400">
            <span>
              Vectors:{' '}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {Math.round((kbIndexStats.vectorized / kbIndexStats.total) * 100)}%
              </span>
            </span>
            <span>
              Algolia:{' '}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {Math.round((kbIndexStats.algoliaSynced / kbIndexStats.total) * 100)}%
              </span>
            </span>
          </div>
        </div>
      )}

      {!loading && (
        <div className="mb-5 rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              {allDocumentTagLabels.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Tags
                  </p>
                  <div className="flex flex-col gap-2">
                    <div
                      className={`flex flex-wrap items-center gap-2 ${
                        clipTagFirstRow ? 'max-h-[2.625rem] overflow-hidden' : ''
                      }`}
                    >
                      {commonTagChips.map(({ label, count }) => renderTagFilterChip(label, count))}
                      {onlySingletonTags
                        ? rareTagChips.map(({ label, count }) => renderTagFilterChip(label, count))
                        : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {hasRareTagChips ? (
                        <button
                          type="button"
                          onClick={() => setTagRareExpanded((v) => !v)}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                        >
                          {tagRareExpanded ? (
                            <>
                              Hide rare tags
                              <ChevronUp className="h-3.5 w-3.5 opacity-70" aria-hidden />
                            </>
                          ) : (
                            <>
                              Rare tags ({rareTagChips.length})
                              <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
                            </>
                          )}
                        </button>
                      ) : null}
                      {tagFilterLabels.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setTagFilterLabels([])}
                          className="text-xs font-medium text-slate-500 underline decoration-slate-300 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          Clear filters
                        </button>
                      ) : null}
                    </div>
                    {hasRareTagChips && commonTagChips.length > 0 && tagRareExpanded ? (
                      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2 dark:border-slate-800">
                        {rareTagChips.map(({ label, count }) => renderTagFilterChip(label, count))}
                      </div>
                    ) : null}
                    {visibleTagChips.length === 0 && tagFilterLabels.length > 0 ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        No overlapping tags in this set — use Clear filters to widen.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    Tags
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    No tags yet. Open a document, choose <span className="font-medium">Edit</span>, and add
                    tags there — they will show up here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700 dark:border-slate-600 dark:border-t-slate-300" />
        </div>
      ) : files.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow-sm dark:bg-slate-900 dark:shadow-slate-900/50">
          <FileText className="mx-auto mb-4 h-16 w-16 text-slate-300 dark:text-slate-600" />
          <p className="text-lg text-slate-500 dark:text-slate-400">No markdown files yet</p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="mt-4 font-medium text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
          >
            Create your first file
          </button>
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white py-12 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <p className="text-slate-600 dark:text-slate-300">No files match the selected tags.</p>
          <button
            type="button"
            onClick={() => setTagFilterLabels([])}
            className="mt-3 text-sm font-medium text-slate-800 underline dark:text-slate-200"
          >
            Clear tag filters
          </button>
        </div>
      ) : viewMode === 'panel' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredFiles.map((file) => {
            const tagLabels = documentTagStrings(file);
            return (
            <div
              key={file.id}
              role="button"
              tabIndex={0}
              onClick={() => onViewDocument(file.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onViewDocument(file.id);
                }
              }}
              aria-label={`Open ${file.title}`}
              className="cursor-pointer rounded-lg border border-transparent bg-white p-5 shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600 dark:focus-visible:ring-offset-slate-950"
            >
              <div className="mb-3 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="min-w-0 flex-1 text-lg font-semibold text-slate-800 dark:text-slate-100">
                    {file.title}
                  </h3>
                </div>
                {file.summary?.trim() ? (
                  <p className="mt-1 text-xs leading-snug text-slate-500 line-clamp-3 dark:text-slate-400">
                    {file.summary.trim()}
                  </p>
                ) : null}
                <p className="mt-1 text-[11px] text-slate-400 tabular-nums dark:text-slate-500">
                  {new Date(file.created_at).toLocaleDateString()}
                </p>
              </div>
              {(tagLabels.length > 0 || hasIndexBadges(file)) && (
                <div
                  className="mb-3 flex flex-col gap-1.5"
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  {tagLabels.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {tagLabels.map((label) => (
                        <span
                          key={`${file.id}-${label}`}
                          className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {hasIndexBadges(file) ? <IndexStatusChips file={file} /> : null}
                </div>
              )}
              <div className="flex items-center justify-end">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(file);
                    }}
                    className="rounded p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(file);
                    }}
                    className="rounded p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-800 dark:text-red-400 dark:hover:bg-red-950/50 dark:hover:text-red-300"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <table className="w-full">
            <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/90">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Tags
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {filteredFiles.map((file) => {
                const tagLabels = documentTagStrings(file);
                return (
                <tr
                  key={file.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onViewDocument(file.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onViewDocument(file.id);
                    }
                  }}
                  aria-label={`Open ${file.title}`}
                  className="cursor-pointer transition-colors hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 dark:hover:bg-slate-800/60 dark:focus-visible:bg-slate-800/60"
                >
                  <td className="px-6 py-4">
                    <div className="min-w-0 max-w-xl">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-slate-800 dark:text-slate-100">{file.title}</span>
                      </div>
                      {file.summary?.trim() ? (
                        <p className="mt-1 text-xs leading-snug text-slate-500 line-clamp-2 dark:text-slate-400">
                          {file.summary.trim()}
                        </p>
                      ) : null}
                      <p className="mt-1 text-[11px] text-slate-400 tabular-nums dark:text-slate-500">
                        {new Date(file.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex max-w-xs flex-col gap-1.5">
                      {tagLabels.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {tagLabels.map((label) => (
                            <span
                              key={`${file.id}-${label}`}
                              className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {hasIndexBadges(file) ? <IndexStatusChips file={file} /> : null}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setJsonModalFile(file);
                        }}
                        className="rounded p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                        title="View raw JSON"
                      >
                        <Braces className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteModal(file);
                        }}
                        className="rounded p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-800 dark:text-red-400 dark:hover:bg-red-950/50 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-slate-800">New Markdown File</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="mb-4">
                <label className="flex items-center justify-center w-full p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-slate-400 transition-colors">
                  <input
                    type="file"
                    accept=".md"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Upload className="w-5 h-5 mr-2 text-slate-600" />
                  <span className="text-slate-600">Upload .md file</span>
                </label>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  placeholder="Enter title..."
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Content
                </label>
                <textarea
                  value={uploadContent}
                  onChange={(e) => setUploadContent(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent font-mono text-sm"
                  rows={12}
                  placeholder="Paste or type your markdown content..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-slate-50">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Create File
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-slate-800">Edit File</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Content
                </label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent font-mono text-sm"
                  rows={12}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-slate-50">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && selectedFile && (
        <DeleteModal
          chatTitle={selectedFile.title}
          onDelete={handleDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedFile(null);
          }}
        />
      )}

      {jsonModalFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setJsonModalFile(null)}
          role="presentation"
        >
          <div
            className="flex max-h-[min(90vh,40rem)] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-slate-900 dark:ring-1 dark:ring-slate-700"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="json-modal-title"
          >
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
              <div className="min-w-0">
                <h2
                  id="json-modal-title"
                  className="text-lg font-semibold text-slate-900 dark:text-slate-100"
                >
                  Raw JSON
                </h2>
                <p className="mt-1 truncate text-sm text-slate-600 dark:text-slate-400">
                  {jsonModalFile.title}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setJsonModalFile(null)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-4">
              <pre className="whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-xs leading-relaxed text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                {JSON.stringify(jsonModalFile, null, 2)}
              </pre>
            </div>
            <div className="flex justify-end border-t border-slate-200 px-4 py-3 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setJsonModalFile(null)}
                className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
